import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Heart, Lock, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      verifyToken(tokenFromUrl);
    } else {
      setVerifying(false);
      setTokenValid(false);
    }
  }, [searchParams]);

  const verifyToken = async (resetToken) => {
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-reset-token/${resetToken}`);
      if (res.ok) {
        setTokenValid(true);
      } else {
        setTokenValid(false);
      }
    } catch (error) {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token,
          new_password: password
        })
      });

      if (res.ok) {
        setResetSuccess(true);
        toast.success('Password reset successfully!');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAF9] to-[#F4E4BA]/20 flex items-center justify-center p-6">
        <Card className="w-full max-w-md bg-white rounded-xl border border-stone-100 shadow-[0_8px_24px_rgba(124,154,146,0.15)] p-8">
          <div className="text-center space-y-4">
            <Heart className="w-12 h-12 text-primary fill-primary mx-auto animate-pulse" />
            <p className="text-[#57534E]">Verifying reset link...</p>
          </div>
        </Card>
      </div>
    );
  }

  // Invalid or expired token
  if (!tokenValid && !resetSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAF9] to-[#F4E4BA]/20 flex items-center justify-center p-6">
        <Card className="w-full max-w-md bg-white rounded-xl border border-stone-100 shadow-[0_8px_24px_rgba(124,154,146,0.15)] p-8">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-nunito font-bold text-[#2D2D2D]">Invalid Reset Link</h2>
            <p className="text-[#57534E]">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <Button
              onClick={() => navigate('/auth')}
              className="w-full bg-primary text-white hover:bg-primary/90 rounded-full h-12 font-semibold mt-4"
            >
              Back to Sign In
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Success state
  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAF9] to-[#F4E4BA]/20 flex items-center justify-center p-6">
        <Card className="w-full max-w-md bg-white rounded-xl border border-stone-100 shadow-[0_8px_24px_rgba(124,154,146,0.15)] p-8">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-nunito font-bold text-[#2D2D2D]">Password Reset!</h2>
            <p className="text-[#57534E]">
              Your password has been successfully reset. You can now sign in with your new password.
            </p>
            <Button
              onClick={() => navigate('/auth')}
              className="w-full bg-primary text-white hover:bg-primary/90 rounded-full h-12 font-semibold mt-4"
              data-testid="back-to-signin"
            >
              Sign In Now
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAFAF9] to-[#F4E4BA]/20 flex items-center justify-center p-6">
      <Card className="w-full max-w-md bg-white rounded-xl border border-stone-100 shadow-[0_8px_24px_rgba(124,154,146,0.15)] p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="text-center space-y-2">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl font-nunito font-bold text-[#2D2D2D]">Set New Password</h2>
            <p className="text-[#57534E]">
              Enter your new password below
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-[#2D2D2D] font-medium">New Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="bg-stone-50 border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-lg h-12 pr-10"
                placeholder="Enter new password"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-[#A8A29E]">Must be at least 6 characters</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-[#2D2D2D] font-medium">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="bg-stone-50 border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-lg h-12"
              placeholder="Confirm new password"
              data-testid="confirm-password-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white hover:bg-primary/90 rounded-full h-12 font-semibold"
            data-testid="reset-password-submit"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
